/**
 * FinanceAuditViewPage — Issue #402.
 *
 * 统一财务审计视图：跨资金 / 工资 / 提成 / 渠道分成 / 行政采购 / 冲正事件等
 * 14 类来源的一个只读列表 + reveal 申请 + 导出。
 *
 * 约束：
 *   - 所有数据通过 @jinzhuan/api-client；禁止前端假状态。
 *   - 金额摘要按作用域可见：boss / finance 的 read_full、员工的 read_self
 *     与渠道的 read_channel 都能看到金额本身；reveal 仅控制银行账号 /
 *     身份证 / 手机号 / 工资明细 / 税号等敏感明文字段。
 *   - 后端若仍以 `[restricted]` 占位返回（向后兼容），前端展示为「需 reveal」
 *     而不是 0 或空字符串。
 *   - reveal 仅记录审计日志；明文仍由源域 reveal 接口提供。
 *   - 导出走 authenticated fetch；CSV 首行包含审计水印。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  canExportFinanceAuditView,
  canRevealFinanceAuditEntry,
  canSeeFinanceAuditView,
} from '@/lib/financePermissions';
import {
  downloadFinanceAuditViewCsv,
  useFinanceAuditReveal,
  useFinanceAuditView,
  FINANCE_AUDIT_SOURCE_TYPES,
  type FinanceAuditEntryDto,
  type FinanceAuditSourceType,
} from '@/hooks/useFinanceAuditView';

const SOURCE_LABELS: Record<FinanceAuditSourceType, string> = {
  payment_record: '收款流水',
  invoice_record: '发票',
  installment_item: '分期项',
  finance_adjustment: '退款/调整',
  payment_application: '付款申请',
  employee_advance: '员工借支',
  expense_reimbursement: '费用报销',
  advance_offset: '借支冲销',
  commission_ledger: '提成台账',
  channel_commission_item: '渠道分成',
  payroll_row: '工资行',
  payroll_payout: '工资付款',
  admin_procurement: '行政采购',
  settlement_event: '结算事件',
};

const SOURCE_STATUS_LABELS: Record<string, string> = {
  connected: '已连接',
  partial: '部分接入',
  pending_source: '待数据源接入',
  not_connected: '未接入',
};

const RESTRICTED_MARKER = '[restricted]';

interface RevealDialogState {
  readonly entry: FinanceAuditEntryDto;
  readonly reason: string;
  readonly approvalReference: string;
  readonly fields: string;
}

// Static Tailwind class branches per project rule (`AI GUIDE.md` §11):
// dynamic class string interpolation prevents Tailwind's content-scan from
// emitting these utilities, so we render branches instead.
function SourceStatusBadge({ status }: { status: string }) {
  const label = SOURCE_STATUS_LABELS[status] ?? status;
  if (status === 'connected') {
    return <span className="text-xs font-medium text-green-700">{label}</span>;
  }
  if (status === 'partial' || status === 'pending_source') {
    return <span className="text-xs font-medium text-amber-600">{label}</span>;
  }
  return <span className="text-xs font-medium text-gray-400">{label}</span>;
}

function AmountCell({ value }: { value: string }) {
  if (value === RESTRICTED_MARKER) {
    return <span className="text-xs italic text-amber-600">[需 reveal]</span>;
  }
  return <span className="tabular-nums">{value}</span>;
}

export default function FinanceAuditViewPage() {
  const { permissions } = useAuth();
  const canSee = canSeeFinanceAuditView(permissions);
  const canReveal = canRevealFinanceAuditEntry(permissions);
  const canExport = canExportFinanceAuditView(permissions);

  const [selectedTypes, setSelectedTypes] = useState<
    ReadonlySet<FinanceAuditSourceType>
  >(new Set());
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(50);
  const [exportReason, setExportReason] = useState<string>('');
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [revealDialog, setRevealDialog] = useState<RevealDialogState | null>(
    null,
  );
  const [revealMessage, setRevealMessage] = useState<string | null>(null);

  const { data, loading, error, fetchList } = useFinanceAuditView();
  const { loading: revealLoading, error: revealError, reveal } =
    useFinanceAuditReveal();

  const filterTypes = useMemo<
    ReadonlyArray<FinanceAuditSourceType> | undefined
  >(() => {
    if (selectedTypes.size === 0) return undefined;
    return Array.from(selectedTypes);
  }, [selectedTypes]);

  useEffect(() => {
    if (!canSee) return;
    void fetchList({ sourceTypes: filterTypes, page, limit });
  }, [canSee, fetchList, filterTypes, page, limit]);

  const handleToggleType = useCallback((t: FinanceAuditSourceType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
    setPage(1);
  }, []);

  const handleExport = useCallback(async () => {
    if (!exportReason.trim()) {
      setExportError('请填写导出原因（用于审计）');
      return;
    }
    setExportError(null);
    setExporting(true);
    try {
      await downloadFinanceAuditViewCsv({
        reason: exportReason.trim(),
        filter: { sourceTypes: filterTypes, page, limit },
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : '导出失败');
    } finally {
      setExporting(false);
    }
  }, [exportReason, filterTypes, page, limit]);

  const handleRevealSubmit = useCallback(async () => {
    if (!revealDialog) return;
    const fieldsArr = revealDialog.fields
      .split(',')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
    const result = await reveal({
      sourceType: revealDialog.entry.sourceType,
      sourceId: revealDialog.entry.sourceId,
      reason: revealDialog.reason,
      approvalReference: revealDialog.approvalReference,
      fields: fieldsArr,
    });
    if (result) {
      setRevealMessage(
        `已记录审计：${result.auditLogId}。明文请通过源域 reveal 接口取得。`,
      );
      setRevealDialog(null);
    }
  }, [reveal, revealDialog]);

  if (!canSee) {
    return (
      <div className="rounded-md border border-gray-100 p-6 text-sm text-gray-500">
        当前账号没有访问统一财务审计视图的权限。请联系管理员申请
        finance.audit_view:read_full / read_team / read_self / read_channel 之一。
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold">统一财务审计视图</h1>
        <p className="text-sm text-gray-500">
          跨收款 / 退款 / 工资 / 提成 / 渠道分成 / 行政采购 / 冲正事件的只读审计
          视图。本视图不修改任何源记录状态；reveal / 导出均写入审计日志。
        </p>
        {data?.scope && (
          <p className="text-xs text-gray-400">
            当前作用域：<span className="font-medium">{data.scope.scope}</span>
            ；reveal：{data.scope.canReveal ? '允许' : '不允许'}；
            导出：{data.scope.canExport ? '允许' : '不允许'}
          </p>
        )}
      </header>

      <section
        aria-label="来源筛选"
        className="flex flex-wrap items-center gap-2 rounded-md border border-gray-100 p-3"
      >
        <span className="text-xs text-gray-500">来源筛选：</span>
        {FINANCE_AUDIT_SOURCE_TYPES.map((t) => {
          const active = selectedTypes.has(t);
          // Static class branches keep Tailwind's content-scan happy.
          const activeClass =
            'rounded-full px-2 py-1 text-xs bg-indigo-600 text-white';
          const inactiveClass =
            'rounded-full px-2 py-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200';
          return (
            <button
              key={t}
              type="button"
              onClick={() => handleToggleType(t)}
              className={active ? activeClass : inactiveClass}
            >
              {SOURCE_LABELS[t]}
            </button>
          );
        })}
        {selectedTypes.size > 0 && (
          <button
            type="button"
            className="text-xs text-gray-500 underline"
            onClick={() => {
              setSelectedTypes(new Set());
              setPage(1);
            }}
          >
            清除
          </button>
        )}
      </section>

      {canExport && (
        <section
          aria-label="导出审计 CSV"
          className="flex flex-wrap items-center gap-2 rounded-md border border-gray-100 bg-amber-50 p-3"
        >
          <label className="text-xs text-gray-600" htmlFor="export-reason">
            导出原因（写入审计水印）：
          </label>
          <input
            id="export-reason"
            type="text"
            className="rounded border border-gray-200 px-2 py-1 text-sm"
            value={exportReason}
            onChange={(e) => setExportReason(e.target.value)}
            placeholder="例如：月度结算复核"
            maxLength={200}
          />
          <button
            type="button"
            className="rounded bg-amber-600 px-3 py-1 text-sm text-white hover:bg-amber-700 disabled:opacity-50"
            disabled={exporting}
            onClick={() => void handleExport()}
          >
            {exporting ? '导出中…' : '导出 CSV'}
          </button>
          {exportError && (
            <span className="text-xs text-red-600">{exportError}</span>
          )}
        </section>
      )}

      {data?.sourceStatuses && data.sourceStatuses.length > 0 && (
        <section
          aria-label="数据源接入状态"
          className="rounded-md border border-gray-100 p-3"
        >
          <h2 className="text-xs font-medium text-gray-500">数据源接入状态</h2>
          <ul className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3 md:grid-cols-4">
            {data.sourceStatuses.map((s) => (
              <li key={s.sourceType} className="flex items-center gap-2">
                <span className="text-gray-600">{SOURCE_LABELS[s.sourceType] ?? s.sourceType}：</span>
                <SourceStatusBadge status={s.sourceStatus} />
                {s.reason && <span className="text-gray-400">{s.reason}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="审计条目列表" className="overflow-x-auto rounded-md border border-gray-100">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {[
                '来源',
                '业务编号',
                '金额',
                '状态',
                '业务时间',
                '审批人',
                '关联短码',
                '事件',
                '数据状态',
                '敏感字段',
                '操作',
              ].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={11} className="px-3 py-4 text-center text-xs text-gray-400">
                  加载中…
                </td>
              </tr>
            )}
            {!loading && data?.data?.length === 0 && (
              <tr>
                <td colSpan={11} className="px-3 py-4 text-center text-xs text-gray-400">
                  当前作用域下没有审计条目
                </td>
              </tr>
            )}
            {data?.data?.map((entry) => (
              <tr key={entry.entryKey} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-xs">{SOURCE_LABELS[entry.sourceType] ?? entry.sourceType}</td>
                <td className="px-3 py-2 text-xs">{entry.sourceCode}</td>
                <td className="px-3 py-2 text-xs">
                  <AmountCell value={entry.amount} />
                </td>
                <td className="px-3 py-2 text-xs">{entry.status}</td>
                <td className="px-3 py-2 text-xs">
                  {entry.occurredAt
                    ? new Date(entry.occurredAt).toLocaleString('zh-CN')
                    : '-'}
                </td>
                <td className="px-3 py-2 text-xs">{entry.approvalActorId ?? '-'}</td>
                <td className="px-3 py-2 text-xs">{entry.counterpartyShortCode ?? '-'}</td>
                <td className="px-3 py-2 text-xs">
                  {entry.settlementEvent ? (
                    <>
                      <div>{entry.settlementEvent.eventType}</div>
                      <div className="text-gray-400">
                        {new Date(entry.settlementEvent.occurredAt).toLocaleString('zh-CN')}
                      </div>
                    </>
                  ) : (
                    '-'
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  <SourceStatusBadge status={entry.sourceStatus} />
                </td>
                <td className="px-3 py-2 text-xs">
                  {entry.sensitiveMarker.requiresReveal ? (
                    <span className="text-amber-600">
                      {entry.sensitiveMarker.maskedFields.join('/')}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {entry.sensitiveMarker.requiresReveal && canReveal && (
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() =>
                        setRevealDialog({
                          entry,
                          reason: '',
                          approvalReference: '',
                          fields: entry.sensitiveMarker.maskedFields.join(','),
                        })
                      }
                    >
                      申请 reveal
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {data && (
        <footer className="flex items-center justify-between text-xs text-gray-500">
          <span>
            共 {data.total} 条；第 {data.page} 页，每页 {data.limit} 条；
            {data.capturedAt && (
              <>
                聚合时间：{new Date(data.capturedAt).toLocaleString('zh-CN')}
              </>
            )}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="rounded border border-gray-200 px-2 py-1 disabled:opacity-50"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              上一页
            </button>
            <button
              type="button"
              className="rounded border border-gray-200 px-2 py-1 disabled:opacity-50"
              disabled={loading || (data.data?.length ?? 0) < limit}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </button>
          </span>
        </footer>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-600">
          列表加载失败：{error.message}
        </div>
      )}
      {revealMessage && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-xs text-green-700">
          {revealMessage}
        </div>
      )}

      {revealDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-[480px] rounded-md bg-white p-4 shadow-xl">
            <h2 className="text-sm font-semibold">
              申请 reveal — {SOURCE_LABELS[revealDialog.entry.sourceType]} ·
              {' '}
              {revealDialog.entry.sourceCode}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              reveal 申请仅记录审计日志；明文请通过源域 reveal 接口取得
              （如 finance.payee_account:reveal / finance.payroll:reveal）。
            </p>
            <div className="mt-3 space-y-2 text-xs">
              <label className="block">
                <span className="text-gray-700">原因（必填）</span>
                <input
                  className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
                  value={revealDialog.reason}
                  onChange={(e) =>
                    setRevealDialog({ ...revealDialog, reason: e.target.value })
                  }
                  maxLength={255}
                />
              </label>
              <label className="block">
                <span className="text-gray-700">二次验证 / 审批参考号（必填）</span>
                <input
                  className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
                  value={revealDialog.approvalReference}
                  onChange={(e) =>
                    setRevealDialog({
                      ...revealDialog,
                      approvalReference: e.target.value,
                    })
                  }
                  maxLength={128}
                />
              </label>
              <label className="block">
                <span className="text-gray-700">要 reveal 的字段（逗号分隔）</span>
                <input
                  className="mt-1 w-full rounded border border-gray-200 px-2 py-1"
                  value={revealDialog.fields}
                  onChange={(e) =>
                    setRevealDialog({ ...revealDialog, fields: e.target.value })
                  }
                />
              </label>
            </div>
            {revealError && (
              <div className="mt-2 text-xs text-red-600">{revealError.message}</div>
            )}
            <div className="mt-3 flex justify-end gap-2 text-xs">
              <button
                type="button"
                className="rounded border border-gray-200 px-2 py-1"
                onClick={() => setRevealDialog(null)}
              >
                取消
              </button>
              <button
                type="button"
                className="rounded bg-indigo-600 px-3 py-1 text-white disabled:opacity-50"
                disabled={revealLoading}
                onClick={() => void handleRevealSubmit()}
              >
                {revealLoading ? '提交中…' : '提交 reveal 申请'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
